import { DestroyRef, inject, Injectable } from '@angular/core';
import { HttpHapService } from '@bo-client/core/http/http-hap.service';
import { EntityService } from '@bo-client/shared/dynamic-search-serverside/entity-service';
import { combineLatest, Observable, of, switchMap } from 'rxjs';
import { GridRegistrationModel } from '@bo-client/activation-management/grid-registration/model/grid-registration.model';
import { path } from '@bo-client/shared/constants/path';
import { Router } from '@angular/router';
import { SearchSortDtoModel } from '@bo-client/shared/dynamic-search-serverside/dynamic-search.model';
import { AuthorizationService } from '@bo-client/core/authorization/authorization.service';
import { RowActionModel } from '@bo-client/shared/entity-list-page/cell-renderers/actions-cell-renderer/RowActionModel';
import { map, shareReplay } from 'rxjs/operators';
import { CommonStatusEnum } from '@bo-client/activation-management/shared/models/commissioningModel';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { MatDialogSize } from '@bo-client/shared/constants/mat-dialog-size';
import { GenericTwoOptionsModalComponent } from '@bo-client/shared/modals/generic-two-options-modal/generic-two-options-modal.component';
import { ToastrService } from 'ngx-toastr';
import { uri } from '@bo-client/shared/constants/uri';
import { CreatableAction } from '@bo-client/shared/entity-list-page/entity-list-page.component';
import {
  GridRegistrationTypeConst,
  GridRegistrationTypeEnum
} from '@bo-client/activation-management/grid-registration/model/grid-registration.enum';
import { DataShareService } from '@bo-client/shared/services/data-share.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Injectable({
  providedIn: 'root'
})
export class GridRegistrationService implements EntityService {
  private authorizationService = inject(AuthorizationService);
  private toastService = inject(ToastrService);
  private matDialog = inject(MatDialog);
  private dataShareService = inject(DataShareService);
  private destroyRef = inject(DestroyRef);

  // eslint-disable-next-line no-empty-function
  constructor(private httpHapService: HttpHapService, private router: Router) {}

  baseUrl: string;

  loadData(
    tenant: string,
    environment: string,
    page: number,
    size: number,
    filterModel: any,
    columnState: any[]
  ): Observable<{
    content: any[];
    totalElements: number;
  }> {
    const result = columnState
      .filter((item) => item.sort)
      .map((item) => ({ colId: item.colId, sort: item.sort }));

    const filterAndSort = {
      filterModel: filterModel,
      sortModel: result
    };

    return this.httpHapService.postTyped<{
      content: any[];
      totalElements: number;
    }>(filterAndSort, `/gridregistration/filter/v2?page=${page}&size=${size}`);
  }

  // Todo: Nimmt Netzanmeldungen für alle Installateure, auch wenn der jeweilige Installateur nicht die Berechtigungen darauf hat -> Entweder Backend anpassen oder alten Endpunkt verwenden, sobald der Filter funktioniert
  getRegistrationsWithSearchModel(searchSortDto: SearchSortDtoModel, pageSize = 10) {
    return this.httpHapService.postTyped<{
      content: GridRegistrationModel[];
      totalElements: number;
    }>(searchSortDto, `/gridregistration/filter?page=0&size=${pageSize}`);
  }

  navigateToCreatePage(params: any) {}

  getEditUrl(id: number | string): string {
    return path.cmPrefix + '/' + path.cmGridRegistration + path.edit + '/' + id;
  }

  navigateToEditPage(id: any) {
    this.router.navigate([this.getEditUrl(id)], {
      replaceUrl: false
    });
  }

  private deleteGridRegistration(gridRegistration: GridRegistrationModel, onSuccess: () => void) {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.disableClose = true;
    dialogConfig.id = 'gridRegistration-into-list-delete';
    dialogConfig.maxHeight = MatDialogSize.maxHeight;
    dialogConfig.width = MatDialogSize.width;
    dialogConfig.data = {
      title: 'Netzanmeldung löschen?',
      message: 'Sind Sie sicher, dass Sie die Netzanmeldung löschen möchten?',
      optionA: 'Abbrechen',
      optionB: 'Löschen bestätigen'
    };
    this.matDialog
      .open(GenericTwoOptionsModalComponent, dialogConfig)
      .afterClosed()
      .pipe()
      .subscribe({
        next: (result) => {
          if (result) {
            if (result == 'optionB') {
              this.deleteRegister(gridRegistration, onSuccess);
            }
          }
        },
        error: () => {
          this.toastService.error('Beim Löschen ist ein Fehler aufgetreten');
        }
      });
  }

  private deleteRegister(gridRegistration: GridRegistrationModel, onSuccess: () => void) {
    this.httpHapService.delete(uri.cmGridRegistration + '/' + gridRegistration.id).subscribe({
      next: () => {
        this.toastService.success('Anfrage für Netzanmeldung erfolgreich gelöscht.');
        onSuccess();
      },
      error: () => {
        this.toastService.error('Beim Löschen ist ein Fehler aufgetreten');
      }
    });
  }

  getAvailableListActions$ = (): Observable<RowActionModel<GridRegistrationModel>[]> => {
    const permissions$ = combineLatest({
      read: this.authorizationService.hasPermission$('GRID_REGISTRATION_READ'),
      write: this.authorizationService.hasPermission$('GRID_REGISTRATION_UPDATE'),
      delete: this.authorizationService.hasPermission$('GRID_REGISTRATION_DELETE')
    });
    const isKeyUser$ = this.authorizationService.hasRole$('ROLE_KEYUSER_GRIDREGISTRATION');
    const isInstaller$ = this.authorizationService.isInstaller$;

    return combineLatest([isKeyUser$, isInstaller$, permissions$]).pipe(
      takeUntilDestroyed(this.destroyRef),
      map(([isKeyUser, isInstaller, permissions]) => [
        ...(!permissions.write && permissions.read
          ? [
              {
                icon: 'search',
                tooltip: 'Ansehen',
                action: (gridRegistration) => this.navigateToEditPage(gridRegistration.id)
              }
            ]
          : []),
        ...(permissions.write
          ? [
              {
                icon: 'edit',
                tooltip: 'Bearbeiten',
                action: (gridRegistration) => this.navigateToEditPage(gridRegistration.id)
              }
            ]
          : []),
        ...(permissions.delete && (isKeyUser || isInstaller)
          ? [
              {
                icon: 'delete',
                iconColor: 'warn',
                tooltip: 'Löschen',
                action: (gridRegistration, onSuccess) =>
                  this.deleteGridRegistration(gridRegistration, onSuccess),
                isVisible: (gridRegistration) =>
                  isKeyUser$ || (isInstaller && gridRegistration.state === CommonStatusEnum.Incomplete)
              } as RowActionModel<GridRegistrationModel>
            ]
          : [])
      ])
    );
  };

  getCreatableActions$(): Observable<CreatableAction[]> {
    const canCreate$ = this.authorizationService.hasPermission$('GRID_REGISTRATION_UPDATE');
    const isInstaller$ = this.authorizationService.isInstaller$.pipe(takeUntilDestroyed(this.destroyRef));
    const availableGridRegistrationOptions$ = combineLatest([
      this.dataShareService.tenantConfig$,
      isInstaller$
    ]).pipe(
      map(([tenantConfig, isInstaller]) => {
        const blacklist: string[] = [];

        // Fügt PlugInProducer zur Blacklist hinzu, wenn die Prozess-Konfig zum Ausblenden des neuen Plug-In-Produzenten für Installateure aktiviert ist.
        if (isInstaller && !!tenantConfig.gridRegistration?.hideNewPlugInProducerButton) {
          blacklist.push(GridRegistrationTypeEnum.PlugInProducer);
        }
        if (
          !!tenantConfig.gridRegistration?.hideChangeMeteringConceptButton ||
          !!tenantConfig.gridRegistration?.changeMeteringConcept?.hideChangeMeteringConceptButton
        ) {
          blacklist.push(GridRegistrationTypeEnum.ChangeMeteringConcept);
        }

        // Filtert alle Netzanmeldungstypen, die in der Blacklist enthalten sind.
        return GridRegistrationTypeConst.filter((item) => !blacklist.includes(item.value));
      })
    );

    return canCreate$.pipe(
      takeUntilDestroyed(this.destroyRef),
      switchMap((canCreate) =>
        canCreate
          ? availableGridRegistrationOptions$
          : of(
              [] as {
                value: GridRegistrationTypeEnum;
                displayName: string;
                imgUrl: string;
              }[]
            )
      ),
      map((availableOptions) => {
        return availableOptions.map(
          (option) =>
            ({
              iconName: 'add',
              title: option.displayName,
              description: 'Erstellen Sie einen neuen Antrag auf Netzanmeldung vom Typ ' + option.displayName,
              action: () =>
                this.router.navigate([
                  path.cmPrefix + '/' + path.cmGridRegistration + path.create + '/' + option.value
                ])
            } as CreatableAction)
        );
      }),
      shareReplay(1)
    );
  }
}
